import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import ReactGA from 'react-ga';
import axios from 'axios';

// Bootstrap Components
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Alert,
    Badge,
    Spinner,
    InputGroup,
    ListGroup
} from 'react-bootstrap';

// Custom Components
import Footer from '../components/Footer';
import Menu from '../components/Navbar';
import MyToast from '../components/MyToast';
import MySpinner from '../components/MySpinner';
import InfoModal from '../components/Modal/InfoModal';

// Services & Context
import {
    savePayment,
    saveSubscription,
    updateSubscription,
    generateUniqueMongoDbID,
    listCoupons,
    sendMailWithNodemailer,
    initPawapayPayment
} from '../services/api';
import { auth } from "../firebase";
import { DeviseConvContext } from '../Contexts/DeviseConvertContext';

// External Libraries
import CurrencyFormat from 'react-currency-format';

// Styles
import './PaymentSubscription.css';
import './CountrySelection.css';

// Constants
const PAYMENT_OPTIONS = {
    'ORANGE': '64d16cb1bf64e00384b76a4c',
    'MTN': '64d16cb2bf64e00384b76a4e'
};

const MESOMB_API_BASE_URL = 'https://fwabl598k2.execute-api.us-west-1.amazonaws.com/dev';

const COUNTRIES = [
    { code: 'BENIN', name: 'BÉNIN' },
    { code: 'BURKINA_FASSO', name: 'BURKINA FASSO' },
    { code: 'CAMEROUN', name: 'CAMEROUN' },
    { code: 'CONGO', name: 'CONGO' },
    { code: 'COTE_DIVOIRE', name: 'CÔTE D\'IVOIRE' },
    { code: 'GABON', name: 'GABON' },
    { code: 'RD_CONGO', name: 'RD CONGO' },
    { code: 'SENEGAL', name: 'SÉNÉGAL' }
];

const P_Subscription = () => {
    // Router hooks
    const location = useLocation();
    const navigate = useNavigate();
    const offer = location.state?.offer || null;

    // Context
    const valDollar = useContext(DeviseConvContext);

    // Initial state
    const initialPaymentData = useMemo(() => ({
        phone: '',
        methode_paiement: 'ORANGE',
        country: 'Cameroun'
    }), []);

    // State management
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true); // Nouveau state pour gérer le chargement auth
    const [paymentDatas, setPaymentDatas] = useState(initialPaymentData);
    const [offerData, setOfferData] = useState(null);
    const [amount_cfa, setAmount_cfa] = useState(null);

    // UI State
    const [visible, setVisible] = useState(false);
    const [msg, setMsg] = useState('');
    const [title, setTitle] = useState('');
    const [img, setImg] = useState(null);

    // Coupon State
    const [coupon, setCoupon] = useState(null);
    const [couponMsg, setCouponMsg] = useState('');
    const [couponValidity, setCouponValidity] = useState(false);
    const [currentCoupon, setCurrentCoupon] = useState(null);
    const [coupons, setCoupons] = useState([]);

    // Process State
    const [inProgress, setinProgress] = useState(false);
    const [submissionStep, setSubmissionStep] = useState('');
    const [openToast, setOpenToast] = useState({
        open: false,
        msg: '',
        severity: ''
    });

    // Country Selection State
    const [showCountrySelection, setShowCountrySelection] = useState(true);
    const [selectedCountry, setSelectedCountry] = useState('CAMEROUN');

    // ============= UTILITY FUNCTIONS =============

    const covertAmountToCfa = useCallback((price) => {
        return parseFloat(price) * (parseFloat(valDollar) || 555);
    }, [valDollar]);

    const applyPercentage = useCallback((px) => {
        const price_ = parseFloat(px);
        const percentage = parseInt(currentCoupon?.amount || 0);
        const realAmount = price_ - (price_ * percentage / 100);
        return Math.round(realAmount);
    }, [currentCoupon]);

    const navigateToHome = useCallback(() => {
        navigate('/accueil');
    }, [navigate]);

    // ============= EVENT HANDLERS =============

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setPaymentDatas(prevData => {
            if (prevData[name] === value) return prevData;
            return { ...prevData, [name]: value };
        });
    }, []);

    const handleCountrySelect = useCallback((countryCode) => {
        setSelectedCountry(countryCode);
    }, []);

    const handleCouponChange = useCallback((e) => {
        setCoupon(e.target.value);
    }, []);

    // ============= API FUNCTIONS =============

    const initiateMesombPaymentAPI = useCallback(async (paymentID, amount, service, payer) => {
        try {
            const response = await axios.post(`${MESOMB_API_BASE_URL}/mesombpayment/initiate`, {
                paymentID,
                amount,
                service,
                payer
            });
            return response.data;
        } catch (error) {
            console.error('Erreur lors de l\'initiation du paiement:', error);
            throw error;
        }
    }, []);

    const getMesombPaymentStatusAPI = useCallback(async (paymentID) => {
        try {
            const response = await axios.get(`${MESOMB_API_BASE_URL}/mesombpayment/status/${paymentID}`);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la vérification du statut:', error);
            throw error;
        }
    }, []);

    const checkPaymentStatus = useCallback(async (paymentID, maxAttempts = 100) => {
        let attempts = 0;

        return new Promise((resolve, reject) => {
            const checkStatus = async () => {
                try {
                    if (attempts >= maxAttempts) {
                        reject(new Error("Délai d'attente dépassé pour la vérification du paiement"));
                        return;
                    }

                    attempts++;
                    setSubmissionStep(`Vérification du paiement (tentative ${attempts}/${maxAttempts})...`);

                    const response = await getMesombPaymentStatusAPI(paymentID);

                    if (!response.success || !response.data || !response.data.length) {
                        setTimeout(checkStatus, 3000);
                        return;
                    }

                    const transaction = response.data[0];

                    if (transaction.status === 'SUCCESS') {
                        resolve(transaction);
                    } else if (transaction.status === 'FAILED') {
                        reject(new Error("Le paiement a échoué"));
                    } else if (transaction.status === 'STARTED') {
                        setSubmissionStep("En attente de votre validation du paiement...");
                        setTimeout(checkStatus, 3000);
                    } else {
                        setTimeout(checkStatus, 3000);
                    }
                } catch (error) {
                    console.error("Erreur lors de la vérification du statut:", error);
                    setTimeout(checkStatus, 3000);
                }
            };

            checkStatus();
        });
    }, [getMesombPaymentStatusAPI]);

    // ============= MAIN HANDLERS =============

    const handleContinueCountrySelection = useCallback(async () => {
        // Vérifier si l'utilisateur est connecté
        if (!user) {
            setOpenToast({
                open: true,
                msg: 'Vous devez être connecté pour continuer. Redirection vers la page de connexion...',
                severity: 'warning'
            });
            setTimeout(() => navigate('/login'), 2000);
            return;
        }

        if (selectedCountry === 'CAMEROUN') {
            setShowCountrySelection(false);
        } else {
            try {
                setOpenToast({ open: false, msg: '', severity: '' });
                setinProgress(true);

                const startDate = new Date();
                const paymentID = (await generateUniqueMongoDbID()).data.data;

                const subscription = await saveSubscription({
                    offerID: offerData._id,
                    userID: user.uid,
                    startDate: startDate.toISOString(),
                    endDate: new Date(startDate.getTime() + parseInt(offerData.duration) * 24 * 60 * 60 * 1000).toISOString(),
                    subscriptionStatus: 'PENDING'
                });

                const subscriptionID = subscription.data.data?._id;

                const pawapayLinkData = await initPawapayPayment({
                    amount: parseInt(amount_cfa),
                    reason: `Paiement pour l'offre en ${offerData?.name}`,
                    paymentID: paymentID,
                    subscriptionID: subscriptionID,
                    statementDescription: `Offre en ${offerData?.name}`
                });

                setinProgress(false);

                if (pawapayLinkData.data.data?.redirectUrl) {
                    window.open(pawapayLinkData.data.data?.redirectUrl, '_blank');

                    sendMailWithNodemailer({
                        from: 'services@exams-tcfcanada.com',
                        to_email: `examstcfcanada@gmail.com`,
                        from_name: 'EXAMS TCF CANADA',
                        subject: "DEBUT DE PAIEMENT PAR PAWAPAY",
                        text: `Un utilisateur vient de cliquer sur payer avec pawapay sur le site web. Les détails sont les suivants:\n\nEmail: ${user.email}\nOffre: ${offerData?.name}\nPaymentID: ${paymentID}\nSubscriptionID: ${subscriptionID}`
                    });

                    setTimeout(() => navigate('/accueil'), 2000);
                } else {
                    setOpenToast({
                        open: true,
                        msg: "Une erreur s'est produite lors de l'opération. Veuillez nous contacter sur WhatsApp si l'erreur persiste.",
                        severity: 'error'
                    });
                }

            } catch (error) {
                setinProgress(false);
                setOpenToast({
                    open: true,
                    msg: "Une erreur s'est produite lors de l'opération. Veuillez nous contacter sur WhatsApp si l'erreur persiste.",
                    severity: 'error'
                });
            }
        }
    }, [selectedCountry, offerData, user, amount_cfa, navigate]);

    const handlePaySubscription = useCallback(async (e) => {
        e.preventDefault();

        // Vérifier si l'utilisateur est connecté
        if (!user) {
            setOpenToast({
                open: true,
                msg: 'Vous devez être connecté pour effectuer un paiement. Redirection vers la page de connexion...',
                severity: 'warning'
            });
            setTimeout(() => navigate('/login'), 2000);
            return;
        }

        const { phone, methode_paiement } = paymentDatas;

        if (!phone || !methode_paiement) {
            setOpenToast({
                open: true,
                msg: 'Choisir une méthode de paiement, puis entrer un numéro de téléphone valide',
                severity: 'error'
            });
            return;
        }

        let normalizedPhone = phone;
        if (phone.startsWith('237') && phone.length === 12) {
            normalizedPhone = phone.substring(3);
        }

        if (normalizedPhone.length !== 9 || !normalizedPhone.startsWith('6')) {
            setOpenToast({
                open: true,
                msg: 'Veuillez entrer un numéro de téléphone valide ! Formats acceptés: 6XXXXXXXX ou 237XXXXXXXXX',
                severity: 'error'
            });
            return;
        }

        try {
            setinProgress(true);
            setSubmissionStep('Préparation de votre souscription...');

            const startDate = new Date();
            const paymentID = (await generateUniqueMongoDbID()).data.data;

            setSubmissionStep('Enregistrement de votre souscription...');
            const subscription = await saveSubscription({
                offerID: offerData._id,
                userID: user.uid,
                startDate: startDate.toISOString(),
                endDate: new Date(startDate.getTime() + parseInt(offerData.duration) * 24 * 60 * 60 * 1000).toISOString(),
                subscriptionStatus: 'PENDING'
            });

            if (couponValidity === true && applyPercentage(amount_cfa) < 10) {
                setSubmissionStep('Application de votre code de réduction...');

                await updateSubscription({
                    subscriptionID: subscription.data.data._id,
                    couponID: currentCoupon?._id,
                    subscriptionStatus: 'ACTIVED'
                });

                setinProgress(false);
                setOpenToast({
                    open: true,
                    msg: 'Souscription réussie !',
                    severity: 'success'
                });

                setTimeout(navigateToHome, 3500);
            } else {
                setTitle('Validation du paiement');
                setImg("../assets/img/payment-sms.png");
                setMsg('Pour valider le paiement veuillez suivre les instructions du SMS que vous allez recevoir sur votre mobile ');
                setVisible(true);

                setSubmissionStep('Initialisation de votre paiement...');
                await initiateMesombPaymentAPI(
                    paymentID,
                    applyPercentage(amount_cfa),
                    methode_paiement,
                    normalizedPhone
                );

                try {
                    setSubmissionStep('En attente de votre validation du paiement...');
                    await checkPaymentStatus(paymentID);

                    setSubmissionStep('Finalisation de votre souscription...');

                    await savePayment({
                        paymentOptionID: PAYMENT_OPTIONS[methode_paiement],
                        amount_dollar: applyPercentage(offerData.price),
                        subscriptionID: subscription.data.data._id,
                        paymentStatus: 'SUCCESS',
                        userID: user.uid,
                        amount_cfa: applyPercentage(amount_cfa),
                        paymentID
                    });

                    await updateSubscription({
                        subscriptionID: subscription.data.data._id,
                        couponID: currentCoupon?._id,
                        subscriptionStatus: 'ACTIVED'
                    });

                    setinProgress(false);
                    setOpenToast({
                        open: true,
                        msg: 'Souscription réussie !',
                        severity: 'success'
                    });

                    setTimeout(navigateToHome, 3500);

                    sendMailWithNodemailer({
                        from: 'services@exams-tcfcanada.com',
                        to_email: `examstcfcanada@gmail.com`,
                        from_name: 'Abonnement',
                        subject: 'Nouvel abonnement',
                        text: `Un nouveau abonnement à l'offre ${offerData?.name || ''} de ${offerData?.price || ''}`
                    });

                } catch (paymentError) {
                    console.error("Erreur de paiement:", paymentError);

                    await savePayment({
                        paymentOptionID: PAYMENT_OPTIONS[methode_paiement],
                        amount_dollar: applyPercentage(offerData.price),
                        subscriptionID: subscription.data.data._id,
                        paymentStatus: 'FAILED',
                        userID: user.uid,
                        amount_cfa: applyPercentage(amount_cfa),
                        paymentID
                    });

                    await updateSubscription({
                        subscriptionID: subscription.data.data._id,
                        couponID: currentCoupon?._id,
                        subscriptionStatus: 'FAILED'
                    });

                    setinProgress(false);
                    setOpenToast({
                        open: true,
                        msg: 'Échec du paiement. Veuillez vérifier votre solde et réessayer.',
                        severity: 'error'
                    });
                }
            }

        } catch (error) {
            console.log(error);
            setinProgress(false);
            setOpenToast({
                open: true,
                msg: 'Echec d\'activation de l\'abonnement. Veuillez vérifier votre solde et si le problème persiste réessayez ultérieurement',
                severity: 'error'
            });
        }
    }, [paymentDatas, offerData, user, amount_cfa, couponValidity, applyPercentage, currentCoupon, initiateMesombPaymentAPI, checkPaymentStatus, navigateToHome, navigate]);

    // ============= EFFECTS =============

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (authUser) => {
            setUser(authUser || null);
            setAuthLoading(false); // Marquer que l'authentification est terminée
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!offer) {
            navigate('/error');
            return;
        }
        setOfferData(JSON.parse(offer));
    }, [offer, navigate]);

    useEffect(() => {
        ReactGA.pageview('/souscription');
        ReactGA.event({
            category: 'Session',
            action: 'Start',
            label: 'souscription'
        });

        return () => {
            ReactGA.event({
                category: 'Session',
                action: 'End',
                label: 'souscription'
            });
        };
    }, []);

    useEffect(() => {
        listCoupons({ status: 'ACTIVED' }).then(resp => {
            setCoupons(resp.data.data || []);
        });
    }, []);

    useEffect(() => {
        if (offerData) {
            setAmount_cfa(covertAmountToCfa(offerData.price));
        }
    }, [offerData, covertAmountToCfa]);

    useEffect(() => {
        let exists = false;
        let coupon_tmp = null;

        for (const coup of coupons) {
            if (coup.name === coupon) {
                coupon_tmp = coup;
                exists = true;
                break;
            }
        }

        if (exists) {
            setCouponMsg('Coupon de réduction accepté');
            setCouponValidity(true);
            setCurrentCoupon(coupon_tmp);
        } else {
            setCouponMsg('Coupon invalide ou expiré');
            setCouponValidity(false);
            setCurrentCoupon(null);
        }
    }, [coupon, coupons]);

    // ============= RENDER COMPONENTS =============

    const CountrySelectionComponent = useMemo(() => (
        <Container fluid className="country-selection-wrapper mt-6">
            <Row className="justify-content-center align-items-center min-vh-100">
                <Col xs={12} md={8} lg={6} xl={5}>
                    <Alert variant="warning" className="mb-4">
                        <Alert.Heading as="h6">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Important
                        </Alert.Heading>
                        <p className="mb-0">
                            L'option de paiement mobile n'est pas disponible dans tous les Pays pour le moment.
                            Si vous ne trouvez pas votre Pays dans la liste, choisissez le paiement par carte bancaire.
                            En cas de difficulté, contactez-nous sur WhatsApp:
                            <a href='https://wa.me/18192530943' className="ms-1">
                                https://wa.me/18192530943
                            </a>
                        </p>
                    </Alert>

                    <Card className="shadow-lg border-0">
                        <Card.Body className="p-4">
                            <div className="text-center mb-4">
                                <h1 className="h3 fw-bold text-primary mb-3">
                                    Choisir votre Pays pour le paiement
                                </h1>
                            </div>

                            <div>
                                <Form.Label className="fw-semibold mb-3">
                                    Choix du Pays
                                </Form.Label>

                                <ListGroup className="mb-4">
                                    {COUNTRIES.map((country) => (
                                        <ListGroup.Item
                                            key={country.code}
                                            action
                                            active={selectedCountry === country.code}
                                            onClick={() => handleCountrySelect(country.code)}
                                            className="d-flex align-items-center cursor-pointer"
                                        >
                                            <Form.Check
                                                type="radio"
                                                id={`country-${country.code}`}
                                                name="country"
                                                checked={selectedCountry === country.code}
                                                onChange={() => handleCountrySelect(country.code)}
                                                className="me-3"
                                            />
                                            <span className="fw-medium">
                                                {country.name}
                                            </span>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>

                                <Button
                                    variant="primary"
                                    size="lg"
                                    className="w-100 fw-semibold"
                                    onClick={handleContinueCountrySelection}
                                    disabled={inProgress}
                                >
                                    {inProgress ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                className="me-2"
                                            />
                                            Traitement en cours...
                                        </>
                                    ) : (
                                        'Continuer'
                                    )}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    ), [selectedCountry, handleCountrySelect, handleContinueCountrySelection, inProgress]);

    const PaymentComponent = useMemo(() => (
        <Container fluid className="payment-subscription-wrapper">
            <Row className="justify-content-center">
                <Col xs={12} md={8} lg={6} xl={5}>
                    <div className="text-center text-white mb-5">
                        <h1 className="display-5 fw-bold mb-3">Paiement sécurisé</h1>
                        <p className="lead">Complétez votre souscription via Mobile Money</p>
                    </div>

                    <Card className="shadow-lg border-0 bg-white bg-opacity-95">
                        <Card.Body className="p-4">
                            {/* Order Summary */}
                            <Card className="bg-light border-0 mb-4">
                                <Card.Body className="p-3">
                                    <h5 className="fw-bold mb-3">Résumé de commande</h5>

                                    <div className="d-flex justify-content-between mb-2">
                                        <span>{offerData?.name}</span>
                                        <span className="fw-semibold">${offerData?.price}</span>
                                    </div>

                                    <div className="d-flex justify-content-between mb-3">
                                        <span>Taxe</span>
                                        <span className="text-success fw-semibold">$0.00</span>
                                    </div>

                                    <hr />

                                    <div className="d-flex justify-content-between">
                                        <span className="fw-bold">Total</span>
                                        <span className="fw-bold text-primary fs-5">
                                            ${offerData?.price}
                                        </span>
                                    </div>
                                </Card.Body>
                            </Card>

                            <Form onSubmit={handlePaySubscription}>
                                {/* Payment Method */}
                                <div className="mb-4">
                                    <Form.Label className="fw-semibold mb-3">
                                        Méthode de paiement
                                    </Form.Label>

                                    <Row className="g-3">
                                        <Col xs={6}>
                                            <Form.Check
                                                type="radio"
                                                name="methode_paiement"
                                                value="ORANGE"
                                                id="orange"
                                                checked={paymentDatas.methode_paiement === 'ORANGE'}
                                                onChange={handleChange}
                                                className="payment-method-card"
                                                label={
                                                    <div className="text-center p-3">
                                                        <img
                                                            src="../assets/img/orange_money.png"
                                                            alt="Orange Money"
                                                            width="40"
                                                            className="mb-2"
                                                        />
                                                        <div className="fw-medium">Orange Money</div>
                                                    </div>
                                                }
                                            />
                                        </Col>

                                        <Col xs={6}>
                                            <Form.Check
                                                type="radio"
                                                name="methode_paiement"
                                                value="MTN"
                                                id="mtn"
                                                checked={paymentDatas.methode_paiement === 'MTN'}
                                                onChange={handleChange}
                                                className="payment-method-card"
                                                label={
                                                    <div className="text-center p-3">
                                                        <img
                                                            src="../assets/img/mtn_mobile_money.png"
                                                            alt="MTN Mobile Money"
                                                            width="40"
                                                            className="mb-2"
                                                        />
                                                        <div className="fw-medium">MTN Mobile Money</div>
                                                    </div>
                                                }
                                            />
                                        </Col>
                                    </Row>
                                </div>

                                {/* Phone Number */}
                                <div className="mb-4">
                                    <Form.Label className="fw-semibold">
                                        Numéro de téléphone
                                    </Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>+237</InputGroup.Text>
                                        <Form.Control
                                            type="tel"
                                            name="phone"
                                            value={paymentDatas.phone || ''}
                                            onChange={handleChange}
                                            placeholder="6XXXXXXXX"
                                            required
                                        />
                                    </InputGroup>
                                </div>

                                {/* Coupon */}
                                <div className="mb-4">
                                    <Form.Label className="fw-semibold">
                                        Code de réduction (optionnel)
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={coupon || ''}
                                        onChange={handleCouponChange}
                                        placeholder="Entrez votre code de réduction"
                                    />

                                    {coupon && coupon !== "" && (
                                        <div className={`mt-2 p-2 rounded ${couponValidity ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'}`}>
                                            <small className="fw-medium">
                                                <i className={`fas fa-${couponValidity ? 'check' : 'times'} me-2`}></i>
                                                {couponMsg}
                                            </small>
                                        </div>
                                    )}
                                </div>

                                {/* Amount to Pay */}
                                <Card className="bg-primary text-white mb-4">
                                    <Card.Body className="text-center p-3">
                                        <div className="mb-2">Montant à payer :</div>
                                        <div className="fs-4 fw-bold">
                                            <CurrencyFormat
                                                prefix="$"
                                                value={`${applyPercentage(offerData?.price || 0)}`}
                                                displayType={'text'}
                                                thousandSeparator={true}
                                            />
                                        </div>
                                        <div className="small opacity-75">
                                            (<CurrencyFormat
                                                value={`${applyPercentage(amount_cfa || 0)}`}
                                                displayType={'text'}
                                                thousandSeparator={true}
                                                suffix=" XAF"
                                            />)
                                        </div>
                                    </Card.Body>
                                </Card>

                                {/* Processing Status */}
                                {submissionStep && (
                                    <Alert variant="info" className="d-flex align-items-center mb-4">
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            className="me-3"
                                        />
                                        <span>{submissionStep}</span>
                                    </Alert>
                                )}

                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    variant="success"
                                    size="lg"
                                    className="w-100 fw-semibold mb-3"
                                    disabled={inProgress}
                                >
                                    {inProgress ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                className="me-2"
                                            />
                                            Traitement en cours...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-credit-card me-2"></i>
                                            Confirmer & Payer
                                        </>
                                    )}
                                </Button>

                                {/* Terms */}
                                <p className="text-center text-muted small mb-0">
                                    En cliquant sur <strong>Confirmer & Payer</strong> vous acceptez les{' '}
                                    <NavLink to="/politique-confidentialite" className="text-decoration-none">
                                        Termes & Conditions
                                    </NavLink>
                                </p>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    ), [offerData, paymentDatas, handleChange, handlePaySubscription, handleCouponChange, coupon, couponMsg, couponValidity, applyPercentage, amount_cfa, submissionStep, inProgress]);

    // ============= MAIN RENDER =============

    // Afficher un loader pendant que l'authentification se charge
    if (authLoading) {
        return (
            <>
                <Menu />
                <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                        <p className="mt-3 text-muted">Vérification de votre connexion...</p>
                    </div>
                </Container>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Menu />

            {showCountrySelection ? CountrySelectionComponent : PaymentComponent}

            <MyToast
                openToast={openToast}
                setOpenToast={setOpenToast}
            />

            <MySpinner loading={inProgress} />

            <InfoModal
                visible={visible}
                title={title}
                msg={msg}
                setVisible={setVisible}
                img={img}
            />

            <Footer />
        </>
    );
};

export default P_Subscription;